import React, { useContext, useMemo, useState } from 'react';
import styled from 'styled-components';

import Badge from 'kingpin/atoms/Badge';
import Button from 'kingpin/atoms/Button';
import Typography from 'kingpin/atoms/Typography';
import ConfirmationModal from 'components/ConfirmationModal';
import Row from 'components/Common/Row';
import AccountPickerContext from 'contexts/AccountPickerContext';
import CloudFunctionClientContext from 'contexts/CloudFunctionClientContext';
import ToastContext from 'contexts/ToastContext';
import { pinAgentKbVersion, promoteAgentKbVersion } from 'services/agentKb';

import { AgentKbContext } from './AgentKbProvider';
import ImportDialog from './ImportDialog';

// Versions, pins and the live pointer. The point of this tab is that publishing
// and going live are different acts: a version sits here inert until someone
// pins themselves to it (a canary), pins an account to it, or promotes it
// (everyone).
//
// Two lists, deliberately separate. The first is the editor's own lineage —
// Firestore version docs with notes and artefact counts. The second is the
// folders in GCS that this editor did NOT publish: the graphql team uploads
// straight to the bucket, and those versions were previously invisible here.
// Only the difference is listed, so a version never appears twice.
// Either list can be pinned — version resolution hands a bare folder name to
// the loader, which reads it from GCS whether or not we have a doc for it.

const Pane = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px 32px;
`;

const BannerDiv = styled.div`
  padding: 12px 16px;
  border-radius: 8px;
  background-color: var(--color-badge-warning-bg);
  border: 1px solid var(--color-badge-warning-border);
  margin-bottom: 16px;
`;

const VersionCard = styled.div`
  border: 1px solid var(--color-border-secondary);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
`;

const SectionDiv = styled.div`
  margin-top: 32px;
`;

interface PendingAccountPin {
  versionId: string;
  replaces: string | null;
}

const VersionsTab = () => {
  const { api } = useContext(CloudFunctionClientContext);
  const { showToast } = useContext(ToastContext);
  const { selectedAccountId } = useContext(AccountPickerContext);
  const {
    meta,
    versions,
    pins,
    rights,
    currentUserId,
    isLoading,
    isStale,
    bucketLatestVersion,
    bucketVersions,
    refreshServerState,
  } = useContext(AgentKbContext);
  const [pendingAccountPin, setPendingAccountPin] =
    useState<PendingAccountPin | null>(null);
  // Which bucket folder the import dialog is reading. Import is per-version:
  // the endpoint takes one and only falls back to the newest.
  const [importVersion, setImportVersion] = useState<string | null>(null);

  const myPin = pins.find(
    (pin) => pin.scope === 'user' && pin.targetId === currentUserId,
  );
  const accountPin = pins.find(
    (pin) => pin.scope === 'account' && pin.targetId === selectedAccountId,
  );

  const unpublishedBucketVersions = useMemo(() => {
    const publishedIds = new Set(versions.map((version) => version.id));
    return bucketVersions.filter((id) => !publishedIds.has(id));
  }, [versions, bucketVersions]);

  const onPromote = async (versionId: string) => {
    const result = await promoteAgentKbVersion(api, { versionId });
    if (result.ok) {
      showToast(`${versionId} is now live for every account`);
    } else {
      showToast(result.error);
    }
  };

  const onPin = async (versionId: string) => {
    const result = await pinAgentKbVersion(api, {
      pinScope: 'user',
      versionId,
    });
    if (result.ok) {
      showToast(
        versionId
          ? `Your own queries now run against ${versionId}`
          : 'Pin cleared — you are back on the live version',
      );
    } else {
      showToast(result.error);
    }
  };

  // An account pin changes what a customer's agent reads, so it is confirmed
  // rather than fired on click. Clearing one is not: it only returns them to
  // whatever everyone else already gets.
  const applyAccountPin = async (versionId: string) => {
    const result = await pinAgentKbVersion(api, {
      pinScope: 'account',
      versionId,
    });
    if (result.ok) {
      showToast(
        versionId
          ? `This account now runs against ${versionId}`
          : 'Account pin cleared — this account follows the live version again',
      );
      await refreshServerState();
    } else {
      showToast(result.error);
    }
  };

  // Rights and the bucket's newest version come from the server, so hold the
  // whole pane until they land: a promote button that is briefly disabled for
  // permissions nobody has checked yet is worse than a moment's wait.
  if (isLoading) {
    return (
      <Pane data-testid="agent-kb-versions">
        <Typography.Body type="Body 13" color="var(--color-text-secondary)">
          Loading versions…
        </Typography.Body>
      </Pane>
    );
  }

  const accountPinControl = (versionId: string) => {
    const isPinnedHere = accountPin?.versionId === versionId;
    return (
      <Button
        type={isPinnedHere ? 'Secondary' : 'Ghost'}
        size="Small"
        label={isPinnedHere ? 'Unpin account' : 'Pin this account'}
        isDisabled={!rights.canEdit}
        testId={`agent-kb-account-pin-${versionId}`}
        onClick={() => {
          if (isPinnedHere) {
            applyAccountPin('');
            return;
          }
          setPendingAccountPin({
            versionId,
            replaces: accountPin?.versionId ?? null,
          });
        }}
      />
    );
  };

  return (
    <Pane data-testid="agent-kb-versions">
      {isStale && (
        <BannerDiv>
          <Typography.Body type="Body 13">
            The bucket holds version {bucketLatestVersion}, which has never been
            imported here (last imported: {meta?.lastImportedVersion ?? 'none'}
            ). Import it before publishing, or its changes will be dropped.
          </Typography.Body>
        </BannerDiv>
      )}

      {versions.length === 0 && (
        <Typography.Body type="Body 13" color="var(--color-text-secondary)">
          Nothing has been published from this editor yet.
        </Typography.Body>
      )}

      {versions.map((version) => {
        const isLive = version.id === meta?.livePointer;
        const isMine = myPin?.versionId === version.id;
        const isAccounts = accountPin?.versionId === version.id;
        return (
          <VersionCard
            key={version.id}
            data-testid={`agent-kb-version-${version.id}`}
          >
            <Row centerAlign spaceBetween>
              <Row centerAlign style={{ gap: 8 }}>
                <Typography.Body type="Body 13 Bold">
                  {version.id}
                </Typography.Body>
                {isLive && <Badge badgeType="Green" text="Live" />}
                {isMine && <Badge badgeType="Blue" text="Your canary" />}
                {isAccounts && <Badge badgeType="Purple" text="Pinned here" />}
                {version.warnings.length > 0 && (
                  <Badge
                    badgeType="Orange"
                    text={`${version.warnings.length} advisory`}
                  />
                )}
              </Row>
              <Row centerAlign style={{ gap: 8 }}>
                <Button
                  type="Ghost"
                  size="Small"
                  label={isMine ? 'Unpin me' : 'Pin me to this'}
                  onClick={() => onPin(isMine ? '' : version.id)}
                />
                {accountPinControl(version.id)}
                <Button
                  type="Secondary"
                  size="Small"
                  label="Promote"
                  isDisabled={!rights.canPublish || isLive}
                  onClick={() => onPromote(version.id)}
                />
              </Row>
            </Row>
            <Typography.Body type="Body 12" color="var(--color-text-secondary)">
              {version.note || 'No note'} · published by {version.publishedBy} ·{' '}
              {Object.keys(version.artefacts).length} artefact(s)
            </Typography.Body>
          </VersionCard>
        );
      })}

      {unpublishedBucketVersions.length > 0 && (
        <SectionDiv data-testid="agent-kb-bucket-versions">
          <Typography.Header type="H4">
            In the bucket, not published here
          </Typography.Header>
          <div style={{ marginBottom: 12 }}>
            <Typography.Body type="Body 12" color="var(--color-text-secondary)">
              Version folders uploaded to the knowledge-base bucket outside this
              editor, newest first. Import one to read it into this editor —
              that is what the knowledge base browser shows. Pinning is
              separate: it changes which version the agent serves.
            </Typography.Body>
          </div>
          {unpublishedBucketVersions.map((versionId) => {
            const isImported = versionId === meta?.lastImportedVersion;
            const isAccounts = accountPin?.versionId === versionId;
            return (
              <VersionCard
                key={versionId}
                data-testid={`agent-kb-bucket-version-${versionId}`}
              >
                <Row centerAlign spaceBetween>
                  <Row centerAlign style={{ gap: 8 }}>
                    <Typography.Body type="Body 13 Bold">
                      {versionId}
                    </Typography.Body>
                    {isImported ? (
                      <Badge badgeType="Blue" text="Imported" />
                    ) : (
                      <Badge badgeType="Default" text="Never imported" />
                    )}
                    {isAccounts && (
                      <Badge badgeType="Purple" text="Pinned here" />
                    )}
                  </Row>
                  <Row centerAlign style={{ gap: 8 }}>
                    <Button
                      type="Secondary"
                      size="Small"
                      label={isImported ? 'Re-import' : 'Import'}
                      isDisabled={!rights.canEdit}
                      testId={`agent-kb-import-${versionId}`}
                      onClick={() => setImportVersion(versionId)}
                    />
                    {accountPinControl(versionId)}
                  </Row>
                </Row>
              </VersionCard>
            );
          })}
        </SectionDiv>
      )}

      <ConfirmationModal
        isOpen={pendingAccountPin !== null}
        close={() => setPendingAccountPin(null)}
        title="Pin this account to a knowledge base version"
        body={
          pendingAccountPin
            ? `Every Insights answer for this account will be produced against ${
                pendingAccountPin.versionId
              }${
                pendingAccountPin.replaces
                  ? `, replacing its current pin to ${pendingAccountPin.replaces}`
                  : ', instead of the live version'
              }. This changes what the agent knows, not which reports it can see.`
            : undefined
        }
        confirmText="Pin this account"
        onConfirmed={() => {
          if (pendingAccountPin) {
            applyAccountPin(pendingAccountPin.versionId);
          }
          setPendingAccountPin(null);
        }}
      />

      {importVersion && (
        <ImportDialog
          version={importVersion}
          close={() => {
            setImportVersion(null);
            refreshServerState();
          }}
        />
      )}
    </Pane>
  );
};

export default VersionsTab;
